import type { IProjectRepository } from "@domain/repositories";
import { TYPES } from "@utils/inversifyJS/types";
import { inject, injectable } from "inversify";
import type { EffectiveSoftwareVersionContextConfiguration, SoftwareEnumSchema, SoftwareVersionContextConfiguration } from "@domain/schemas/BI";
import type { ProjectDetail, ProjectMoveRequest, ProjectRoleName } from "@domain/schemas/workspace";

/**
Project's own management surface: detail, membership mutations, and the propose/withdraw half of
the move handshake - the accept/reject half lives on `TeamManagementService`, since that authority
belongs to the target team, not the project (see design.md's "two distinct surfaces" decision).
*/
@injectable()
export class ProjectManagementService {
  @inject(TYPES.IProjectRepository)
  private _projectRepository!: IProjectRepository;

  async getProjectAsync(projectId: string): Promise<ProjectDetail> {
    return this._projectRepository.getProjectAsync(projectId);
  }

  async renameProjectAsync(projectId: string, newName: string): Promise<ProjectDetail> {
    return this._projectRepository.renameProjectAsync(projectId, newName);
  }

  async assignProjectRoleAsync(projectId: string, targetUserId: string, role: ProjectRoleName): Promise<ProjectDetail> {
    return this._projectRepository.assignProjectRoleAsync(projectId, targetUserId, role);
  }

  async removeProjectMemberAsync(projectId: string, userId: string): Promise<ProjectDetail> {
    return this._projectRepository.removeProjectMemberAsync(projectId, userId);
  }

  async proposeProjectMoveAsync(sourceTeamId: string, projectId: string, targetTeamId: string): Promise<ProjectMoveRequest> {
    return this._projectRepository.proposeProjectMoveAsync(sourceTeamId, projectId, targetTeamId);
  }

  async withdrawProjectMoveAsync(sourceTeamId: string, requestId: string): Promise<ProjectMoveRequest> {
    return this._projectRepository.withdrawProjectMoveAsync(sourceTeamId, requestId);
  }

  async getSoftwareVersionConfigurationAsync(projectId: string): Promise<SoftwareVersionContextConfiguration> {
    return this._projectRepository.getSoftwareVersionConfigurationAsync(projectId);
  }

  async getEffectiveSoftwareVersionConfigurationAsync(projectId: string): Promise<EffectiveSoftwareVersionContextConfiguration> {
    return this._projectRepository.getEffectiveSoftwareVersionConfigurationAsync(projectId);
  }

  async addSoftwareVersionAsync(projectId: string, software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    return this._projectRepository.addSoftwareVersionAsync(projectId, software, version);
  }

  async removeSoftwareVersionAsync(projectId: string, versionId: string): Promise<SoftwareVersionContextConfiguration> {
    return this._projectRepository.removeSoftwareVersionAsync(projectId, versionId);
  }

  async setSoftwareVersionDefaultAsync(projectId: string, software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    return this._projectRepository.setSoftwareVersionDefaultAsync(projectId, software, version);
  }

  async clearSoftwareVersionDefaultAsync(projectId: string, software: SoftwareEnumSchema): Promise<SoftwareVersionContextConfiguration> {
    return this._projectRepository.clearSoftwareVersionDefaultAsync(projectId, software);
  }
}
